"use client";

import { useEffect, useId, useRef, useState, type CSSProperties } from "react";
import "./studio-agent-avatar.css";
import "./studio-orb-play-button.css";

export type StudioAgentAvatarState =
  | "idle"
  | "thinking"
  | "wondering"
  | "looking"
  | "listening"
  | "focused"
  | "creating"
  | "speaking"
  | "confused"
  | "celebrating";

const BACKGROUNDS: Record<string, string> = {
  lime: "linear-gradient(145deg, var(--cursor-accent), #46a66f)",
  violet: "linear-gradient(145deg, #9b87f5, #6657c8)",
  ocean: "linear-gradient(145deg, #46b9d8, #3374c8)",
  sunset: "linear-gradient(145deg, #f6a15c, #d95d77)",
  rose: "linear-gradient(145deg, #ec7aa5, #ad5ec8)",
  slate: "linear-gradient(145deg, #7f8998, #485260)",
};

/*
 * Idle faces hold still while a touch screen scrolls, then carry on. Their
 * SVG blink/gaze animations restyle and re-lay-out the face every frame,
 * which a phone pays for on every scroll frame. One passive capture listener
 * for the page; touch screens only (hover: none).
 */
const SCROLL_PAUSE_IDLE_MS = 160;
const scrollPausable = new Set<HTMLElement>();
let scrollWatchInstalled = false;
let scrolling = false;
let scrollStartedAt = 0;
let scrollPausedTotalMs = 0;
let scrollEndTimer = 0;
let afterScroll: Array<() => void> = [];

function scrollPausedMs() {
  return scrollPausedTotalMs + (scrolling ? performance.now() - scrollStartedAt : 0);
}

function endScrollPause() {
  window.clearTimeout(scrollEndTimer);
  scrollEndTimer = 0;
  if (!scrolling) return;
  scrolling = false;
  scrollPausedTotalMs += performance.now() - scrollStartedAt;
  for (const el of scrollPausable) el.removeAttribute("data-scroll-paused");
  const queued = afterScroll;
  afterScroll = [];
  for (const run of queued) run();
}

function onAnyScroll() {
  if (!scrolling) {
    scrolling = true;
    scrollStartedAt = performance.now();
    for (const el of scrollPausable) el.setAttribute("data-scroll-paused", "");
  }
  window.clearTimeout(scrollEndTimer);
  scrollEndTimer = window.setTimeout(endScrollPause, SCROLL_PAUSE_IDLE_MS);
}

function installScrollPause() {
  if (scrollWatchInstalled) return;
  scrollWatchInstalled = true;
  if (!window.matchMedia("(hover: none)").matches) return;
  const opts = { capture: true, passive: true } as const;
  document.addEventListener("scroll", onAnyScroll, opts);
  document.addEventListener("scrollend", endScrollPause, opts);
}

export function studioAgentAvatarBackground(background?: string) {
  return BACKGROUNDS[background || ""] ?? BACKGROUNDS.lime;
}

type IdleBeat =
  | "look-left"
  | "look-right"
  | "look-left-across"
  | "look-right-across"
  | "look-up"
  | "look-up-left"
  | "look-up-right"
  | "look-up-across"
  | "look-down"
  | "look-down-left"
  | "look-down-right"
  | "look-down-around"
  | "read"
  | "ponder"
  | "orbit"
  | "surprise"
  | "chuckle"
  | "laugh"
  | "angry";

const IDLE_BEATS: Array<{ id: IdleBeat; ms: number; weight: number }> = [
  { id: "look-left", ms: 3600, weight: 10 },
  { id: "look-right", ms: 3600, weight: 10 },
  { id: "look-left-across", ms: 5400, weight: 8 },
  { id: "look-right-across", ms: 5400, weight: 8 },
  { id: "look-up", ms: 3600, weight: 7 },
  { id: "look-up-left", ms: 3600, weight: 6 },
  { id: "look-up-right", ms: 3600, weight: 6 },
  { id: "look-up-across", ms: 5800, weight: 8 },
  { id: "look-down", ms: 3600, weight: 7 },
  { id: "look-down-left", ms: 3600, weight: 6 },
  { id: "look-down-right", ms: 3600, weight: 6 },
  { id: "look-down-around", ms: 5200, weight: 7 },
  { id: "read", ms: 6400, weight: 9 },
  { id: "ponder", ms: 4200, weight: 7 },
  { id: "orbit", ms: 6000, weight: 6 },
  { id: "surprise", ms: 2800, weight: 5 },
  { id: "chuckle", ms: 3000, weight: 5 },
  { id: "laugh", ms: 3400, weight: 4 },
  { id: "angry", ms: 3200, weight: 4 },
];

function pickIdleBeat(previous: IdleBeat | null): (typeof IDLE_BEATS)[number] {
  const pool = IDLE_BEATS.filter((beat) => beat.id !== previous);
  const total = pool.reduce((sum, beat) => sum + beat.weight, 0);
  let roll = Math.random() * total;
  for (const beat of pool) {
    roll -= beat.weight;
    if (roll <= 0) return beat;
  }
  return pool[0]!;
}

type Props = {
  state?: StudioAgentAvatarState;
  background?: string;
  size?: number;
  label?: string;
  className?: string;
  /** Skip idle-beat timers/paint — use in scrolling lists. */
  staticFace?: boolean;
  /** Agent is working (thinking, tools, helpers): rotating Orb aura ring behind the disk. */
  working?: boolean;
};

export function StudioAgentAvatar({
  state = "idle",
  background,
  size = 30,
  label = "Agent",
  className = "",
  staticFace = false,
  working = false,
}: Props) {
  const clipId = `studio-agent-face-${useId().replace(/:/g, "")}`;
  const [beat, setBeat] = useState<IdleBeat | null>(null);
  const previousBeatRef = useRef<IdleBeat | null>(null);
  const diskRef = useRef<HTMLSpanElement>(null);
  const style = {
    "--studio-agent-avatar-size": `${size}px`,
    "--studio-agent-avatar-bg": studioAgentAvatarBackground(background),
  } as CSSProperties;

  useEffect(() => {
    if (staticFace || state !== "idle") {
      setBeat(null);
      return;
    }
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let cancelled = false;
    let waitId = 0;
    let holdId = 0;
    const schedule = (first: boolean) => {
      const wait = first
        ? 2200 + Math.random() * 2800
        : 2800 + Math.random() * 5200;
      const startBeat = () => {
        if (cancelled) return;
        // No new beat mid-scroll: it would start a fresh animation.
        if (scrolling) {
          afterScroll.push(startBeat);
          return;
        }
        const next = pickIdleBeat(previousBeatRef.current);
        previousBeatRef.current = next.id;
        setBeat(next.id);
        let pausedAtStart = scrollPausedMs();
        const endBeat = () => {
          if (cancelled) return;
          if (scrolling) {
            afterScroll.push(endBeat);
            return;
          }
          // The beat held still during a scroll: give it that time back so
          // it finishes instead of being cut off.
          const owed = scrollPausedMs() - pausedAtStart;
          if (owed > 1) {
            pausedAtStart = scrollPausedMs();
            holdId = window.setTimeout(endBeat, owed);
            return;
          }
          setBeat(null);
          schedule(false);
        };
        holdId = window.setTimeout(endBeat, next.ms);
      };
      waitId = window.setTimeout(startBeat, wait);
    };
    schedule(true);
    return () => {
      cancelled = true;
      window.clearTimeout(waitId);
      window.clearTimeout(holdId);
    };
  }, [state, staticFace]);

  useEffect(() => {
    const el = diskRef.current;
    if (!el || staticFace) return;
    installScrollPause();
    scrollPausable.add(el);
    if (scrolling) el.setAttribute("data-scroll-paused", "");
    return () => {
      scrollPausable.delete(el);
      el.removeAttribute("data-scroll-paused");
    };
  }, [staticFace]);

  const disk = (
    <span
      ref={diskRef}
      className={`studio-agent-avatar is-${state}${staticFace ? " is-static" : ""}${className ? ` ${className}` : ""}`}
      style={style}
      role="img"
      aria-label={`${label} is ${state}`}
      data-agent-state={state}
      data-idle-beat={beat ?? undefined}
    >
      <svg
        viewBox="0 0 48 48"
        width="100%"
        height="100%"
        preserveAspectRatio="xMidYMid meet"
        aria-hidden="true"
      >
        <defs>
          <clipPath id={clipId}>
            <circle cx="24" cy="24" r="20.5" />
          </clipPath>
        </defs>
        <g className="studio-agent-avatar-face">
          <circle cx="24" cy="24" r="20.75" className="studio-agent-avatar-head" />
          <g clipPath={`url(#${clipId})`}>
            <g className="studio-agent-avatar-brows">
              <path
                className="studio-agent-avatar-brow is-left"
                d="M15.1 15.6c1.9-.55 4.3-.6 6.2.12"
              />
              <path
                className="studio-agent-avatar-brow is-right"
                d="M26.7 15.55c1.9-.5 4.3-.55 6.2.14"
              />
            </g>
            <g className="studio-agent-avatar-eyes">
              <g className="studio-agent-avatar-gaze">
                <g className="studio-agent-avatar-eye is-left">
                  <circle cx="19" cy="23.5" r="2.45" />
                </g>
                <g className="studio-agent-avatar-eye is-right">
                  <circle cx="29" cy="23.5" r="2.45" />
                </g>
              </g>
            </g>
          </g>
        </g>
        <g className="studio-agent-avatar-thoughts">
          <circle cx="38" cy="10" r="1.5" />
          <circle cx="41.5" cy="5.5" r="1" />
        </g>
        <g className="studio-agent-avatar-signals">
          <path d="M7 17c-2 4.5-2 9.5 0 14" />
          <path d="M41 17c2 4.5 2 9.5 0 14" />
        </g>
        <g className="studio-agent-avatar-confetti">
          <path d="M8 10l3 2M37 8l-2 3M8 38l3-2M39 36l-3-1" />
          <circle cx="15" cy="7" r="1" />
          <circle cx="34" cy="40" r="1" />
        </g>
      </svg>
    </span>
  );
  if (!working) return disk;
  // Same footprint as the bare disk, so nothing shifts when the ring comes and goes.
  return (
    <span
      className="studio-agent-working"
      style={{ "--studio-agent-avatar-size": `${size}px` } as CSSProperties}
    >
      <span className="studio-agent-working-aura studio-orb-aura studio-orb-aura-a" aria-hidden="true" />
      {disk}
    </span>
  );
}
